/**
 * WorldMapHelp.jsx - Comprehensive Help Modal for World Map
 * Standalone component with inline styles - no external CSS needed
 */

import React from 'react';

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#2a2520',
    borderRadius: '12px',
    border: '2px solid #4a4540',
    maxWidth: '700px',
    width: '100%',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #3a3530',
    backgroundColor: '#322d28',
    borderRadius: '10px 10px 0 0',
  },
  title: {
    margin: 0,
    fontSize: '1.4rem',
    color: '#f0e6d6',
    fontFamily: 'Georgia, serif',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#8a8580',
    fontSize: '1.5rem',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    transition: 'all 0.2s',
  },
  content: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1,
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '1.1rem',
    color: '#d4a055',
    marginBottom: '12px',
    fontFamily: 'Georgia, serif',
    borderBottom: '1px solid #3a3530',
    paddingBottom: '6px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '10px',
  },
  gridItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '8px 10px',
    backgroundColor: '#1e1a18',
    borderRadius: '6px',
    border: '1px solid #3a3530',
  },
  key: {
    color: '#a0d8ef',
    fontWeight: 'bold',
    fontSize: '0.85rem',
    minWidth: '100px',
    flexShrink: 0,
  },
  desc: {
    color: '#c0b8a8',
    fontSize: '0.85rem',
    lineHeight: 1.4,
  },
  row: {
    display: 'flex',
    gap: '14px',
    padding: '12px',
    backgroundColor: '#1e1a18',
    borderRadius: '8px',
    marginBottom: '10px',
    border: '1px solid #3a3530',
  },
  iconLg: {
    fontSize: '1.5rem',
    width: '40px',
    textAlign: 'center',
    flexShrink: 0,
  },
  details: {
    flex: 1,
  },
  detailsTitle: {
    color: '#e8e0d0',
    fontWeight: 'bold',
    marginBottom: '4px',
    fontSize: '0.95rem',
  },
  detailsText: {
    color: '#a09888',
    fontSize: '0.85rem',
    lineHeight: 1.5,
    margin: 0,
  },
  note: {
    backgroundColor: '#2a2824',
    border: '1px solid #4a4540',
    borderLeft: '3px solid #d4a055',
    padding: '10px 14px',
    borderRadius: '0 6px 6px 0',
    marginTop: '12px',
    fontSize: '0.85rem',
    color: '#c8c0b0',
  },
  tipsList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  tipItem: {
    padding: '8px 12px',
    backgroundColor: '#1e1a18',
    borderRadius: '6px',
    marginBottom: '6px',
    fontSize: '0.85rem',
    color: '#b8b0a0',
    border: '1px solid #3a3530',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  tipBullet: {
    color: '#d4a055',
    fontWeight: 'bold',
  },
  pathDemo: {
    display: 'inline-block',
    padding: '2px 12px',
    borderRadius: '4px',
    fontFamily: 'monospace',
    letterSpacing: '2px',
  },
  colorSwatch: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
};

export function WorldMapHelp({ onClose }) {
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>🗺️ World Map Guide</h2>
          <button 
            style={styles.closeBtn} 
            onClick={onClose}
            onMouseOver={e => e.target.style.color = '#fff'}
            onMouseOut={e => e.target.style.color = '#8a8580'}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>

          {/* Navigation */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>🎮 Navigation Controls</h3>
            <div style={styles.grid}>
              <div style={styles.gridItem}>
                <span style={styles.key}>Click + Drag</span>
                <span style={styles.desc}>Pan the map in any direction</span>
              </div>
              <div style={styles.gridItem}>
                <span style={styles.key}>Mouse Wheel</span>
                <span style={styles.desc}>Zoom in/out (50% - 200%)</span>
              </div>
              <div style={styles.gridItem}>
                <span style={styles.key}>🎯 Button</span>
                <span style={styles.desc}>Center view on your location</span>
              </div>
              <div style={styles.gridItem}>
                <span style={styles.key}>+/− Buttons</span>
                <span style={styles.desc}>Zoom controls (20% steps)</span>
              </div>
            </div>
          </div>

          {/* Tile Interaction */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>🖱️ Tile Interaction</h3>
            <div style={styles.grid}>
              <div style={styles.gridItem}>
                <span style={styles.key}>Single Click</span>
                <span style={styles.desc}>Select tile, show preview info</span>
              </div>
              <div style={styles.gridItem}>
                <span style={styles.key}>Double Click</span>
                <span style={styles.desc}>Move to tile (adjacent = instant)</span>
              </div>
              <div style={styles.gridItem}>
                <span style={styles.key}>Double Click Distant</span>
                <span style={styles.desc}>Auto-path with optimal route</span>
              </div>
              <div style={styles.gridItem}>
                <span style={styles.key}>Double Click Waystone</span>
                <span style={styles.desc}>Instant teleport</span>
              </div>
            </div>
          </div>

          {/* Tile Types */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>🏔️ Tile Types</h3>
            
            <div style={styles.row}>
              <span style={styles.iconLg}>🏰</span>
              <div style={styles.details}>
                <div style={styles.detailsTitle}>Kingdom (Blue Border)</div>
                <p style={styles.detailsText}>Your home base. Teleport here from anywhere. Acts as a waystone.</p>
              </div>
            </div>

            <div style={styles.row}>
              <span style={styles.iconLg}>✦</span>
              <div style={styles.details}>
                <div style={styles.detailsTitle}>Waystones (Blue ✦ indicator)</div>
                <p style={styles.detailsText}>Special tiles for instant teleportation. Pathfinding auto-considers these for faster routes.</p>
              </div>
            </div>

            <div style={styles.row}>
              <span style={styles.iconLg}>⛺</span>
              <div style={styles.details}>
                <div style={styles.detailsTitle}>Outposts (Green ⛺ indicator)</div>
                <p style={styles.detailsText}>Established camps. One per biome type. Provides biome bonuses.</p>
              </div>
            </div>

            <div style={styles.row}>
              <span style={styles.iconLg}>🏠</span>
              <div style={styles.details}>
                <div style={styles.detailsTitle}>Tile Buildings (Gold 🏠 indicator)</div>
                <p style={styles.detailsText}>Structures on tiles for ongoing benefits. Cost 2× normal price.</p>
              </div>
            </div>

            <div style={styles.row}>
              <span style={styles.iconLg}>❓</span>
              <div style={styles.details}>
                <div style={styles.detailsTitle}>Unexplored Tiles</div>
                <p style={styles.detailsText}>Adjacent unexplored glow gold. Distant unexplored show as dark fog.</p>
              </div>
            </div>
          </div>

          {/* Pathfinding */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>🛤️ Pathfinding & Auto-Move</h3>
            
            <div style={styles.row}>
              <span style={{...styles.pathDemo, color: '#70c0ff', border: '2px dashed #70c0ff'}}>━ ━ ━</span>
              <div style={styles.details}>
                <div style={styles.detailsTitle}>Blue Dotted Path</div>
                <p style={styles.detailsText}>Route through discovered tiles. Shows step numbers and turn ETA.</p>
              </div>
            </div>

            <div style={styles.row}>
              <span style={{...styles.pathDemo, color: '#ff9050', border: '2px dashed #ff9050'}}>━ ━ ━</span>
              <div style={styles.details}>
                <div style={styles.detailsTitle}>Orange Dotted Path</div>
                <p style={styles.detailsText}>Route includes undiscovered tiles. Random events may occur!</p>
              </div>
            </div>

            <div style={styles.note}>
              💡 <strong>Smart Teleporting:</strong> The system automatically checks if teleporting to a waystone first would make your journey faster!
            </div>
          </div>

          {/* Cooldowns */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>⏱️ Cooldowns</h3>
            <div style={styles.grid}>
              <div style={styles.gridItem}>
                <span style={styles.key}>🚶 Movement</span>
                <span style={styles.desc}>Brief cooldown between moves. Queue moves during cooldown!</span>
              </div>
              <div style={styles.gridItem}>
                <span style={styles.key}>⛏️ Gathering</span>
                <span style={styles.desc}>Cooldown between resource collection. Varies by tile.</span>
              </div>
            </div>
          </div>

          {/* Gathering */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>⛏️ Resource Gathering</h3>
            <div style={styles.grid}>
              <div style={styles.gridItem}>
                <span style={{...styles.colorSwatch, color: '#4ade80', backgroundColor: '#1a2e1a'}}>110%+</span>
                <span style={styles.desc}>High quality - bonus resources</span>
              </div>
              <div style={styles.gridItem}>
                <span style={{...styles.colorSwatch, color: '#9ca3af', backgroundColor: '#1e1e1e'}}>100%</span>
                <span style={styles.desc}>Normal quality - standard yields</span>
              </div>
              <div style={styles.gridItem}>
                <span style={{...styles.colorSwatch, color: '#f87171', backgroundColor: '#2e1a1a'}}>90%-</span>
                <span style={styles.desc}>Low quality - reduced yields</span>
              </div>
            </div>
          </div>

          {/* Building */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>🏗️ Tile Building</h3>
            <p style={{...styles.detailsText, marginBottom: '12px'}}>
              Build on explored tiles for permanent bonuses. <strong style={{color: '#d4a055'}}>Costs 2× normal!</strong>
            </p>
            <div style={styles.row}>
              <div style={styles.details}>
                <div style={styles.detailsTitle}>How to Build:</div>
                <ol style={{margin: '8px 0 0 20px', padding: 0, color: '#a09888', fontSize: '0.85rem', lineHeight: 1.6}}>
                  <li>Move to an explored tile (not Kingdom)</li>
                  <li>Open Build section in side panel</li>
                  <li>Select a building from the list</li>
                  <li>Click "Build" if affordable</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Visual Reference */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>👁️ Visual Reference</h3>
            <div style={styles.grid}>
              <div style={styles.gridItem}>
                <span style={{...styles.key, border: '3px solid #ffd480', padding: '2px 6px', borderRadius: '4px'}}>Gold</span>
                <span style={styles.desc}>Your current location</span>
              </div>
              <div style={styles.gridItem}>
                <span style={{...styles.key, border: '2px solid #80c0ff', padding: '2px 6px', borderRadius: '4px'}}>Blue</span>
                <span style={styles.desc}>Kingdom or Waystone</span>
              </div>
              <div style={styles.gridItem}>
                <span style={{...styles.key, border: '2px solid #80c870', padding: '2px 6px', borderRadius: '4px'}}>Green</span>
                <span style={styles.desc}>Has Outpost</span>
              </div>
              <div style={styles.gridItem}>
                <span style={styles.key}>Compass</span>
                <span style={styles.desc}>Bottom-left orientation</span>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>💡 Pro Tips</h3>
            <ul style={styles.tipsList}>
              <li style={styles.tipItem}>
                <span style={styles.tipBullet}>•</span>
                Discover waystones early for fast travel across the map
              </li>
              <li style={styles.tipItem}>
                <span style={styles.tipBullet}>•</span>
                Queue long journeys - they auto-execute during cooldowns
              </li>
              <li style={styles.tipItem}>
                <span style={styles.tipBullet}>•</span>
                High-quality tiles are worth building on for bonus yields
              </li>
              <li style={styles.tipItem}>
                <span style={styles.tipBullet}>•</span>
                Orange paths warn of undiscovered tiles - events may occur!
              </li>
              <li style={styles.tipItem}>
                <span style={styles.tipBullet}>•</span>
                Single-click tiles to preview before committing to move
              </li>
              <li style={styles.tipItem}>
                <span style={styles.tipBullet}>•</span>
                Use 🎯 button to quickly recenter on yourself
              </li>
            </ul>
          </div>

        </div>
      </div>
    </div>
  );
}

export default WorldMapHelp;
